import React from 'react';
import { isCashAccountType, useCashAccount, useCashAccounts } from '../../../../CashAccount';
import {
  Business,
  CashAccount,
  Investment,
  Loan,
  Maybe,
  NoteReceivable,
  PersonalPropertyItem,
  RealEstate,
} from '../../../../generated/graphql';
import {
  AmountSumTableItemProps,
  AmountTableProps,
  CashAccountsTableContainer,
  CashAccountsTableContainerProps,
  CashAccountSumTableItem,
  CashAccountSumTableItemProps,
  RealEstatePropertyTableContainer,
  RealEstatePropertyTableContainerProps,
  RealEstateSumTableItem,
  RealEstateSumTableItemProps,
} from '../../../../UI/components';
import { Account, AccountType } from '../../../../util';
import { isInvestmentType, useInvestment, useInvestments } from '../../../../Investment';
import { InvestmentSumTableItem, InvestmentSumTableItemProps } from '../../../components/InvestmentSumTableItem';
import {
  InvestmentsTableContainer,
  InvestmentsTableContainerProps,
} from '../../../components/InvestmentsTableContainer';
import { isLoanType, useLoan, useLoans } from '../../../../Loan';
import { LoansTableContainer, LoansTableContainerProps } from '../../../components/LoansTableContainer';
import { LoanSumTableItem, LoanSumTableItemProps } from '../../../components/LoanSumTableItem';
import { useBusiness, useBusinesses } from '../../../../Business';
import { BusinessesTableContainer, BusinessesTableContainerProps } from '../../../components/BusinessesTableContainer';
import { BusinessSumTableItem, BusinessSumTableItemProps } from '../../../components/BusinessSumTableItem';
import { isRealEstateType, useRealEstateProperties, useRealEstateProperty } from '../../../../RealEstateProperty';

type useMergeAccountsPageProps = {
  accountType: AccountType;
};

export type GetAccountFromAccountPropInput = {
  cashAccount?: Maybe<CashAccount>;
  account?: Maybe<Account>;
  investment?: Maybe<Investment>;
  realEstate?: Maybe<RealEstate>;
  loan?: Maybe<Loan>;
  noteReceivable?: Maybe<NoteReceivable>;
  personalPropertyItem?: Maybe<PersonalPropertyItem>;
  business?: Maybe<Business>;
};

type useMergeAccountsPageReturn = {
  useGetAccountsArray: ({ householdID }: { householdID: string }) => Account[];
  useGetTargetAccount: ({
    householdID,
    targetAccountID,
  }: {
    householdID: string;
    targetAccountID: string;
  }) => Account | null | undefined;
  ContainerComponent: React.JSXElementConstructor<
    AmountTableProps & { accountArray: Account[]; children?: (p: { account: Account }) => React.ReactNode }
  >;
  ListItemComponent: React.JSXElementConstructor<
    Pick<
      AmountSumTableItemProps,
      'amount' | 'className' | 'contentPrefix' | 'withChevron' | 'motionProps' | 'subtitle'
    > & { account?: Account | null; onItemClicked: () => void }
  >;
  getAccountFromAccountProp: (input?: Maybe<GetAccountFromAccountPropInput>) => Maybe<Account> | undefined;
};

export const useMergeAccountsPage = ({ accountType }: useMergeAccountsPageProps): useMergeAccountsPageReturn => {
  if (isCashAccountType(accountType)) {
    return {
      useGetAccountsArray: ({ householdID }) => useCashAccounts({ householdID }).cashAccounts,
      useGetTargetAccount: ({ householdID, targetAccountID }) =>
        useCashAccount({ cashAccountID: targetAccountID, householdID }).cashAccount,
      ContainerComponent: ({
        accountArray,
        ...props
      }: Pick<
        CashAccountsTableContainerProps,
        'children' | 'className' | 'hideOnEmpty' | 'isLoading' | 'onItemClicked' | 'title'
      > & { accountArray: Account[] }) => (
        <CashAccountsTableContainer {...props} cashAccountArray={accountArray as CashAccount[]} />
      ),
      ListItemComponent: ({
        account,
        ...props
      }: Pick<
        CashAccountSumTableItemProps,
        | 'amount'
        | 'className'
        | 'contentPrefix'
        | 'itemTitle'
        | 'motionProps'
        | 'subtitle'
        | 'withChevron'
        | 'onItemClicked'
      > & { account?: Account | null }) => <CashAccountSumTableItem {...props} cashAccount={account as CashAccount} />,
      getAccountFromAccountProp: (input) => input?.cashAccount,
    };
  } else if (isInvestmentType(accountType)) {
    return {
      useGetAccountsArray: ({ householdID }) => useInvestments({ householdID }).investments,
      useGetTargetAccount: ({ householdID, targetAccountID }) =>
        useInvestment({ investmentID: targetAccountID, householdID }).investment,
      ContainerComponent: ({
        accountArray,
        ...props
      }: Pick<
        InvestmentsTableContainerProps,
        'children' | 'className' | 'hideOnEmpty' | 'isLoading' | 'onItemClicked' | 'title'
      > & { accountArray: Account[] }) => (
        <InvestmentsTableContainer {...props} investmentArray={accountArray as Investment[]} />
      ),
      ListItemComponent: ({
        account,
        ...props
      }: Pick<
        InvestmentSumTableItemProps,
        | 'amount'
        | 'className'
        | 'contentPrefix'
        | 'itemTitle'
        | 'motionProps'
        | 'subtitle'
        | 'withChevron'
        | 'onItemClicked'
      > & { account?: Account | null }) => <InvestmentSumTableItem {...props} investment={account as Investment} />,
      getAccountFromAccountProp: (input) => input?.investment,
    };
  } else if (isLoanType(accountType)) {
    return {
      useGetAccountsArray: ({ householdID }) => useLoans({ householdID }).loans,
      useGetTargetAccount: ({ householdID, targetAccountID }) => useLoan({ loanID: targetAccountID, householdID }).loan,
      ContainerComponent: ({
        accountArray,
        ...props
      }: Pick<
        LoansTableContainerProps,
        'children' | 'className' | 'hideOnEmpty' | 'isLoading' | 'onItemClicked' | 'title'
      > & { accountArray: Account[] }) => <LoansTableContainer {...props} loanArray={accountArray as Loan[]} />,
      ListItemComponent: ({
        account,
        ...props
      }: Pick<
        LoanSumTableItemProps,
        | 'amount'
        | 'className'
        | 'contentPrefix'
        | 'itemTitle'
        | 'motionProps'
        | 'subtitle'
        | 'withChevron'
        | 'onItemClicked'
      > & { account?: Account | null }) => <LoanSumTableItem {...props} loan={account as Loan} />,
      getAccountFromAccountProp: (input) => input?.loan,
    };
  } else if (accountType === 'Business') {
    return {
      useGetAccountsArray: ({ householdID }) => useBusinesses({ householdID }).businesses,
      useGetTargetAccount: ({ householdID, targetAccountID }) =>
        useBusiness({ businessID: targetAccountID, householdID }).business,
      ContainerComponent: ({
        accountArray,
        ...props
      }: Pick<
        BusinessesTableContainerProps,
        'children' | 'className' | 'hideOnEmpty' | 'isLoading' | 'onItemClicked' | 'title'
      > & { accountArray: Account[] }) => (
        <BusinessesTableContainer {...props} businessArray={accountArray as Business[]} />
      ),
      ListItemComponent: ({
        account,
        ...props
      }: Pick<
        BusinessSumTableItemProps,
        | 'amount'
        | 'className'
        | 'contentPrefix'
        | 'itemTitle'
        | 'motionProps'
        | 'subtitle'
        | 'withChevron'
        | 'onItemClicked'
      > & { account?: Account | null }) => <BusinessSumTableItem {...props} business={account as Business} />,
      getAccountFromAccountProp: (input) => input?.business,
    };
  } else if (isRealEstateType(accountType)) {
    return {
      useGetAccountsArray: ({ householdID }) => useRealEstateProperties({ householdID }).realEstateProperties,
      useGetTargetAccount: ({ householdID, targetAccountID }) =>
        useRealEstateProperty({ realEstatePropertyID: targetAccountID, householdID }).realEstateProperty,
      ContainerComponent: ({
        accountArray,
        ...props
      }: Pick<
        RealEstatePropertyTableContainerProps,
        'children' | 'className' | 'hideOnEmpty' | 'isLoading' | 'title'
      > & { accountArray: Account[] }) => (
        <RealEstatePropertyTableContainer {...props} realEstatePropertyArray={accountArray as RealEstate[]} />
      ),
      ListItemComponent: ({
        account,
        ...props
      }: Pick<
        RealEstateSumTableItemProps,
        | 'amount'
        | 'className'
        | 'contentPrefix'
        | 'itemTitle'
        | 'motionProps'
        | 'subtitle'
        | 'withChevron'
        | 'onItemClicked'
      > & { account?: Account | null }) => <RealEstateSumTableItem {...props} realEstate={account as RealEstate} />,
      getAccountFromAccountProp: (input) => input?.realEstate,
    };
  }
  return {
    useGetAccountsArray: () => [],
    useGetTargetAccount: () => null,
    ContainerComponent: () => <></>,
    ListItemComponent: () => <></>,
    getAccountFromAccountProp: (input) => input?.cashAccount,
  };
};
